/**
 * InlinePhotoAttachments — Iter-176 · Phase 5
 *
 * Photo attachment surface for a single record — mounted inline on
 * service requests and violations. Displays thumbnails with upload
 * date + uploader, and lets staff/PMs add photos (image/*, base64
 * data URL). Reuses generic /api/attachments/{parentKind}/{parentId}
 * endpoint on the backend.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { Button } from './ui/button';
import { Camera, Trash2 } from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

function readAsDataUrl(file) {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(r.result);
    r.onerror = reject;
    r.readAsDataURL(file);
  });
}

function fmtWhen(iso) {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleString(undefined, {
      month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit',
    });
  } catch (_) { return iso; }
}

export default function InlinePhotoAttachments({
  parentKind, parentId, required = false, testIdPrefix,
}) {
  const [items, setItems] = useState([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const inputRef = useRef(null);
  const prefix = testIdPrefix || `${parentKind}-${parentId}`;

  const load = useCallback(async () => {
    if (!parentKind || !parentId) return;
    try {
      const r = await fetch(`${API}/api/attachments/${parentKind}/${parentId}`,
                             { credentials: 'include' });
      if (r.ok) {
        const data = await r.json();
        setItems(Array.isArray(data?.items) ? data.items : []);
        setError(null);
      } else {
        setError(`Could not load photos (${r.status})`);
      }
    } catch (_) {
      setError('Network error loading photos');
    }
  }, [parentKind, parentId]);

  useEffect(() => { load(); }, [load]);

  const upload = async (files) => {
    if (!files || files.length === 0) return;
    setBusy(true);
    try {
      for (const f of Array.from(files)) {
        if (!f.type.startsWith('image/')) continue;
        const dataUrl = await readAsDataUrl(f);
        const r = await fetch(`${API}/api/attachments/${parentKind}/${parentId}`, {
          method: 'POST', credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ photo: dataUrl, filename: f.name || 'photo.jpg' }),
        });
        if (!r.ok) {
          const err = await r.json().catch(() => ({}));
          toast.error(err.detail || 'Upload failed');
          break;
        }
      }
      await load();
      toast.success('Photo added');
    } finally {
      setBusy(false);
      if (inputRef.current) inputRef.current.value = '';
    }
  };

  const remove = async (attachmentId) => {
    try {
      const r = await fetch(`${API}/api/attachments/${attachmentId}`,
                             { method: 'DELETE', credentials: 'include' });
      if (!r.ok) {
        const err = await r.json().catch(() => ({}));
        toast.error(err.detail || 'Could not remove photo');
        return;
      }
      await load();
    } catch (_) { toast.error('Network error'); }
  };

  return (
    <div className="space-y-2" data-testid={`${prefix}-photos`}>
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <p className="text-xs font-bold uppercase tracking-widest text-black">
          Photos {required && <span className="text-red-500">*</span>}
        </p>
        <div>
          <input
            ref={inputRef}
            type="file"
            accept="image/*"
            multiple
            onChange={(e) => upload(e.target.files)}
            className="hidden"
            data-testid={`${prefix}-photos-input`}
          />
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => inputRef.current?.click()}
            disabled={busy}
            className="border-black text-black hover:bg-black hover:text-white text-xs font-bold"
            data-testid={`${prefix}-photos-add`}
          >
            <Camera className="h-3.5 w-3.5 mr-1" />
            {busy ? 'Uploading…' : 'Add photo'}
          </Button>
        </div>
      </div>

      {error && (
        <p className="text-xs text-red-600 font-semibold"
           data-testid={`${prefix}-photos-error`}>{error}</p>
      )}

      {items.length === 0 ? (
        <p className="text-xs italic text-zinc-500"
           data-testid={`${prefix}-photos-empty`}>
          No photos attached yet.
        </p>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2"
             data-testid={`${prefix}-photos-grid`}>
          {items.map((p) => (
            <div key={p.attachment_id}
                 className="rounded overflow-hidden border border-zinc-300 bg-white group relative"
                 data-testid={`${prefix}-photo-${p.attachment_id}`}>
              <img
                src={p.photo}
                alt={p.filename || 'photo'}
                className="w-full h-24 object-cover"
              />
              <div className="p-1.5 text-[10px] leading-tight text-black">
                <p className="font-bold truncate">{p.uploader_name || 'Staff'}</p>
                <p className="text-zinc-500">{fmtWhen(p.created_at)}</p>
              </div>
              <button
                type="button"
                onClick={() => remove(p.attachment_id)}
                className="absolute top-1 right-1 p-1 rounded-full bg-black/60 text-white opacity-0 group-hover:opacity-100 transition"
                aria-label="Remove photo"
                data-testid={`${prefix}-photo-${p.attachment_id}-remove`}
              >
                <Trash2 className="h-3 w-3" />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
